import { filterParamThreeToGPU, paramTexThreeToGL } from "./compat";
import { computeMipmapLevelCount } from "./chunks/mipmap_shader";
import { MipmapPipeline } from "./main/MipmapPipeline";
import THREE from 'three';

function clampToMaxSize(image, maxSize) {

  if (image.width <= maxSize && image.height <= maxSize) {

    return image;

  }

  if (typeof HTMLImageElement !== 'undefined' && image instanceof HTMLImageElement ||
  typeof HTMLCanvasElement !== 'undefined' && image instanceof HTMLCanvasElement ||
  typeof ImageBitmap !== 'undefined' && image instanceof ImageBitmap) {


    // Warning: Scaling through the canvas will only work with images that use
    // premultiplied alpha.

    var maxDimension = Math.max(image.width, image.height);
    var scale = maxSize / maxDimension;

    var newWidth = Math.max(Math.floor(image.width * scale), 1);
    var newHeight = Math.max(Math.floor(image.height * scale), 1);

    var canvas = document.createElement('canvas');
    canvas.width = newWidth;
    canvas.height = newHeight;

    var ctx = canvas.getContext("2d");
    ctx.drawImage(image, 0, 0, image.width, image.height, 0, 0, newWidth, newHeight);

    return canvas;
  } else {
    if ('data' in image) {
      console.warn('THREE.WebGLRenderer: Image in DataTexture is too big (' + image.width + 'x' + image.height + ').');
    }

    return image;
  }

}

/**
 * Creates a descriptor for use with createSampler() for a THREE.Texture.
 * @param {THREE.Texture} texture - The texture to create the sampler descriptor for.
 * @returns {GPUSamplerDescriptor} The sampler descriptor.
 */
export function createSamplerDesc(texture) {
  /** @type {GPUSamplerDescriptor} */
  const samplerDesc = {
    addressModeU: (/** @type {GPUAddressMode} */filterParamThreeToGPU(texture.wrapS)),
    addressModeV: (/** @type {GPUAddressMode} */filterParamThreeToGPU(texture.wrapT)),
    magFilter: (/** @type {GPUFilterMode} */filterParamThreeToGPU(texture.magFilter)),
    minFilter: (/** @type {GPUFilterMode} */filterParamThreeToGPU(texture.minFilter)),
    mipmapFilter: (/** @type {GPUFilterMode} */filterParamThreeToGPU(texture.minFilter, true)),
    maxAnisotropy: texture.anisotropy || 1
  };

  return samplerDesc;
}

/**
 * Returns a stable string key for a sampler descriptor. Must stay in sync with createSamplerDesc.
 * @param {GPUSamplerDescriptor} desc
 * @returns {string}
 */
export function createSamplerKey(desc) {
  return `${desc.addressModeU},${desc.addressModeV},${desc.magFilter},${desc.minFilter},${desc.mipmapFilter},${desc.maxAnisotropy}`;
}

/**
 * Creates a sampler for a THREE.Texture.
 * @param {THREE.Texture} texture
 */
export function createSampler(device, texture) {
  return device.createSampler(createSamplerDesc(texture));
}

/**
 * Recreates the GPUSampler when wrap/filter/anisotropy changed on the texture object.
 * @param {GPUDevice} device
 * @param {THREE.Texture} texture
 */
export function syncGpuSampler(device, texture) {
  if (!texture?.__gpuTexture) {
    return;
  }
  const samplerKey = createSamplerKey(createSamplerDesc(texture));
  if (!texture.__gpuSampler || texture.__gpuSamplerKey !== samplerKey) {
    texture.__gpuSampler = createSampler(device, texture);
    texture.__gpuSamplerKey = samplerKey;
  }
}


export function initCubeMap(device, texture) {

  if (texture?.image.length !== 6) {
    return;
  }

  if (texture.needsUpdate !== true) {
    return;
  }

  let isCompressed = texture instanceof THREE.CompressedTexture;
  let isDataTexture = texture.image[0] instanceof THREE.DataTexture;

  let cubeImage = [];
  for (let i = 0; i < 6; i++) {
    if (!isCompressed && !isDataTexture) {
      cubeImage[i] = clampToMaxSize(texture.image[i], device.limits.maxTextureDimension2D || 8192);
    } else {
      cubeImage[i] = isDataTexture ? texture.image[i].image : texture.image[i];
    }
  }

  if (!texture.__gpuTextureCube) {

    texture.addEventListener('dispose', () => {
      texture.__gpuTextureCube?.destroy();
      texture.__gpuTextureCube = null;
      texture.__gpuSampler = null;
      // The texture might be used in different places that don't know about each other.
      // We set needsUpdate here so that code that still uses a texture that got disposed elsewhere can
      // re-initialize it.
      texture.needsUpdate = true;
    });

    texture.__gpuTextureCube = device.createTexture({
      label: `cube map texture ${texture.uuid}`,
      dimension: "2d",
      format: paramTexThreeToGL(texture.format, texture.type),
      mipLevelCount: cubeImage[0].mipmaps?.length || 1,
      size: [cubeImage[0].width, cubeImage[0].height, 6],
      usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST
    });

    texture.__gpuSampler = createSampler(device, texture);

  }

  for (let i = 0; i < 6; i++) {

    if (!isCompressed) {

      if (isDataTexture) {

        device.queue.writeTexture(
          { texture: texture.__gpuTextureCube, origin: [0, 0, i] },
          cubeImage[i].data,
          { offset: 0, bytesPerRow: cubeImage[i].data.byteLength / cubeImage[i].height },
          [cubeImage[i].width, cubeImage[i].height]
        );

      } else {

        device.queue.copyExternalImageToTexture(
          { source: cubeImage[i], flipY: !!texture.flipY },
          { texture: texture.__gpuTextureCube, origin: [0, 0, i] },
          [cubeImage[i].width, cubeImage[i].height]
        );

      }

      //TODO:
      /*if ( texture.generateMipmaps ) {
            _gl.generateMipmap( _gl.TEXTURE_CUBE_MAP );
        }
       */




    } else {

      let mipmaps = cubeImage[i].mipmaps;

      for (let j = 0, jl = mipmaps.length; j < jl; j++) {

        let mipmap = mipmaps[j];

        device.queue.writeTexture(
          { texture: texture.__gpuTextureCube, origin: [0, 0, i], mipLevel: j },
          mipmap.data,
          { offset: 0, bytesPerRow: mipmap.data.byteLength / mipmap.height },
          [mipmap.width, mipmap.height]
        );
      }

    }

  }

  texture.needsUpdate = false;

  if (texture.onUpdate) texture.onUpdate();

}

/**
 * Whether the existing __gpuTexture is compatible with a new descriptor. GPUTexture size,
 * format, mipLevelCount and usage are all fixed at creation time, so any difference (e.g.
 * setImage() swapping in a differently-sized image) requires a fresh GPUTexture.
 * @param {GPUTexture} gpuTexture
 * @param {GPUTextureDescriptor} descriptor
 */
function isGpuTextureCompatible(gpuTexture, descriptor) {
  return gpuTexture.width === descriptor.size[0] &&
  gpuTexture.height === descriptor.size[1] &&
  gpuTexture.format === descriptor.format &&
  gpuTexture.mipLevelCount === descriptor.mipLevelCount &&
  gpuTexture.usage === descriptor.usage;
}

/**
 * @param {GPUDevice} device
 * @param {MipmapPipeline} mipmapPipeline
 * @param {THREE.Texture} texture
 */
export function initTexture(device, mipmapPipeline, texture) {

  if (texture.needsUpdate !== true) {
    return;
  }

  let isCompressed = texture instanceof THREE.CompressedTexture;
  let isDataTexture = texture instanceof THREE.DataTexture;

  // Whether GPU mipmaps should be auto-generated for this upload. Derived once and never written
  // back to texture.generateMipmaps: THREE consumes that flag, but three.js leaves it set and
  // regenerates mips on every re-upload - consuming it here would silently drop mips on a later
  // setImage() re-upload. Manually supplied mipmaps (mipmaps[]) are uploaded as-is, never generated.
  const hasManualMipmaps = texture.mipmaps?.length > 0;
  const generateMips = texture.generateMipmaps && !isCompressed && !hasManualMipmaps;

  let usage = GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST;
  if (!isCompressed && !isDataTexture) {
    texture.image = clampToMaxSize(texture.image, device.limits.maxTextureDimension2D || 8192);
    usage = usage | GPUTextureUsage.RENDER_ATTACHMENT; //NOTE: RenderAttachment usage seems to be needed in order to call copyExternalImageToTexture
  }
  // Mipmap generation requires RENDER_ATTACHMENT usage (renders to each mip level)
  if (generateMips) {
    usage = usage | GPUTextureUsage.RENDER_ATTACHMENT;
  }

  const mipMapLevelCount = computeMipmapLevelCount(texture.image);

  // Only allocate multi-level mip storage when something will actually fill it.
  const willHaveMips = hasManualMipmaps || generateMips;
  let descriptor = {
    label: texture.name,
    dimension: "2d",
    format: paramTexThreeToGL(texture.format, texture.type),
    mipLevelCount: willHaveMips ? hasManualMipmaps ? texture.mipmaps.length : mipMapLevelCount : 1,
    size: [texture.image.width, texture.image.height],
    usage
  };

  if (texture.__gpuTexture && !isGpuTextureCompatible(texture.__gpuTexture, descriptor)) {
    // e.g. setImage() swapped in an image with different dimensions: the old GPUTexture
    // can't be resized, so it must be destroyed and recreated.
    texture.__gpuTexture.destroy();
    texture.__gpuTexture = null;
    texture.__gpuTextureView = null;
  }

  if (!texture.__gpuTexture) {

    texture.__gpuTexture = device.createTexture(descriptor);

    // In most cases, we just use defaults for creating a view, so we can reuse it.
    // Note that it's not just for performance, but also makes it much easier to detect if a texture has changed. (see MaterialUniforms.js)
    texture.__gpuTextureView = texture.__gpuTexture.createView();

    if (!texture.__gpuDisposeListenerAdded) {
      // Guarded so a reallocation above doesn't register a second listener - each one would
      // destroy the same (current) __gpuTexture again on 'dispose'.
      texture.__gpuDisposeListenerAdded = true;
      texture.addEventListener('dispose', () => {
        texture.__gpuTexture?.destroy();
        texture.__gpuTexture = null;
        texture.__gpuTextureView = null;
        texture.__gpuSampler = null;
      });
    }
  }

  syncGpuSampler(device, texture);

  let image = texture.image;
  let mipmaps = texture.mipmaps;

  if (isDataTexture || isCompressed) {

    // use manually created mipmaps if available
    // if there are no manual mipmaps
    // set 0 level mipmap and then use GL to generate other mipmap levels

    if (mipmaps.length > 0) {

      for (let i = 0, il = mipmaps.length; i < il; i++) {

        let mipmap = mipmaps[i];

        let blockSize = 1;
        if (isCompressed) {
          blockSize = 4;
          if (!descriptor.format.startsWith("bc1-")) {
            console.warn("unknown compressed texture format -- block size might be wrong");
          }
        }

        device.queue.writeTexture(
          { texture: texture.__gpuTexture, origin: [0, 0], mipLevel: i },
          mipmap.data,
          { offset: 0, bytesPerRow: mipmap.data.byteLength / (mipmap.height / blockSize) },
          [Math.max(blockSize, mipmap.width), Math.max(blockSize, mipmap.height)]
        );
      }

    } else {

      device.queue.writeTexture(
        { texture: texture.__gpuTexture, origin: [0, 0] },
        image.data,
        { offset: 0, bytesPerRow: image.data.byteLength / image.height },
        [image.width, image.height]
      );

    }

  } else {// regular Texture (image, video, canvas)

    // use manually created mipmaps if available
    // if there are no manual mipmaps
    // set 0 level mipmap and then use GL to generate other mipmap levels

    if (mipmaps.length > 0) {

      for (let i = 0, il = mipmaps.length; i < il; i++) {

        let mipmap = mipmaps[i];

        device.queue.copyExternalImageToTexture(
          { source: mipmap, flipY: !!texture.flipY },
          { texture: texture.__gpuTexture, mipLevel: i, premultipliedAlpha: !!texture.premultiplyAlpha },
          [mipmap.width, mipmap.height]
        );

      }

    } else {

      device.queue.copyExternalImageToTexture(
        { source: image, flipY: !!texture.flipY },
        { texture: texture.__gpuTexture },
        [image.width, image.height]
      );

    }

  }

  // TODO: optimization?
  if (generateMips) {
    mipmapPipeline.generateMipmaps(texture, descriptor);
  }

  texture.needsUpdate = false;

  if (texture.onUpdate) texture.onUpdate();

}

// (Only) used for textures with unclear ownership, e.g., env map used by multiple renderers.
export function refTexture(texture) {
  if (!(texture instanceof THREE.Texture))
  return;

  texture.__refCount = (texture.__refCount || 0) + 1;
}

export function unrefTexture(texture) {
  if (!(texture instanceof THREE.Texture) || !Number.isFinite(texture.__refCount))
  return;

  if (texture.__refCount < 1) {
    console.warn('Texture was unreferenced more times than referenced.');
  }
  texture.__refCount--;

  if (texture.__refCount < 1) {
    texture.dispose();
  }
}